import React from 'react';
import { MessageSquare, CheckCircle, AlertTriangle } from 'lucide-react';
import { designBadgeFromSaved, DesignBadge } from '../utils/designState';   // 디자인 완료/전 배지 (2026-09-06)
import CarryOverBox from './statement/CarryOverBox';   // 전월 미수 안내 상자 (2026-10-23 1단계 §4-5)
import { remakeOfLabel } from '../utils/statementItems';
import { tidyServiceName } from '../utils/mfImplantPrice';   // 외주 명세서 품목 이름 (2026-11-02)   // 리메이크 '처음 작업' 표기 (2026-10-24 2단계 ④)
import {
    formatStatementDate, formatStatementShortDate, lastDisputeNoteOf, disputedIndexMapOf, STATEMENT_VAT_NOTE,
} from '../utils/statementView';

// 거래명세서 「발행 양식」 단일 소스 (2026-10-06 미리보기 A안)
// - 기본은 읽기 전용: 제목 → 번호/기간 → 공급자/공급받는자 → 항목표(배지 포함) → 합계 → 비고 → 수정 요청 이력 → 정산 완료 표시
// - 상세(ViewTransactionStatement)는 slots로 편집·검토 UI만 끼워 넣는다 — 양식 마크업은 여기 한 곳에서만 바꾼다
// - 미리보기 모달은 slots 없이 그대로 쓴다
//
// props
//   statement           문서(정규화 전 원본 — 공급자·기간·비고·정산 필드 사용)
//   items               정규화된 항목(normalizeStatementItems 결과)
//   totals              computeStatementTotals 결과 { subtotal, discountAmount, total, totalTeeth }
//   lastDisputeNote     (선택) 최근 수정 요청 노트 — 없으면 statement에서 계산
//   rootRef             (선택) .print-area 루트 ref (인쇄용)
//   slots               (선택) 상세 전용 끼움 — 아래 주석 참조. 전부 생략 가능
//   title               (선택) 양식 제목 — 기본 '거 래 명 세 서'. 예상 기공료 모달(ExpectedStatementModal)만 '예 상 기 공 료 (참고용)' (2026-10-07)
//   vatNote             (선택) 합계 아래 부가세 안내 1줄 — 기본 STATEMENT_VAT_NOTE. null/''이면 렌더하지 않음(예상용은 "본 명세서 금액은…"이 거짓 문장이라 숨김)
export const SHEET_ROOT_STYLE = { background: 'white', borderRadius: '12px', border: '1px solid #E2E8F0', padding: '32px 32px 32px 56px' };

function StatementSheet({ statement, items, totals, lastDisputeNote: lastDisputeNoteProp, rootRef, slots = {}, title = '거 래 명 세 서', vatNote = STATEMENT_VAT_NOTE }) {
    const {
        beforeTable,                 // 항목표 위 (검토 안내·편집 툴바·datalist)
        tableProps,                  // <table>에 추가할 props (onKeyDown 등)
        headLead,                    // thead 맨 앞 추가 th (검토 체크박스 열)
        headTrail,                   // thead 맨 뒤 추가 th (편집 삭제 열)
        row = {},                    // 행 단위 끼움
        afterTable,                  // 항목표 아래·합계 위 (편집 할인/메모)
        hideNotes = false,           // 비고 숨김 (편집 모드)
        afterNotes,                  // 비고 아래 (검토 전체 메모)
        hideDisputeHistory = false,  // 수정 요청 이력 블록 숨김 (검토 모드)
        showDisputeRows = true,      // 행 아래 수정 사유 읽기 표시 (검토 모드는 false)
    } = slots;
    const {
        highlight,       // (index) => bool — 행 배경 강조 추가 조건 (검토 체크)
        lead,            // (item, index) => node — 행 맨 앞 td
        trail,           // (item, index) => node — 행 맨 뒤 td
        patient,         // (item, index) => node|undefined — 환자이름 칸 대체(undefined면 기본)
        tooth,           // (item, index) => node|undefined — 치아정보 본문 대체(금값 행 제외)
        count,           // (item, index) => node|undefined — 개수 칸 대체(금값 행 제외)
        unitPrice,       // (item, index) => node|undefined — 개당가격 칸 대체(금값 행 제외)
        after,           // (item, index) => node — 행 바로 아래 추가 행(검토 사유 입력)
        showSourceBadges = true, // 자동수집/수기 배지 (편집 모드는 false)
    } = row;

    const lastDisputeNote = lastDisputeNoteProp !== undefined ? lastDisputeNoteProp : lastDisputeNoteOf(statement);
    // 줄 강조·사유 표시는 '수정 요청됨(disputed)'일 때만 (2026-10-23) — 발행자가 고치면 pending으로 바뀌어 자동으로 꺼지고, 정산 완료 후에도 안 보인다.
    // 줄은 노트에 저장된 키(orderId·환자·치아정보)로 찾는다 (구 노트는 index 폴백)
    const disputedIndexMap = statement?.settlementStatus === 'disputed' ? disputedIndexMapOf(lastDisputeNote, items) : {};
    // 외주 명세서(업체→기공소)만 「품목」 칸 — 품목·임플란트 종류가 serviceName 에만 있다(11-02 대표 "임플란트 종류 등 내용이 없어"). 기공소→치과 명세서 양식은 그대로
    const showItem = statement?.kind === 'outsource_mf';
    const disputeColSpan = 8 + (headTrail ? 1 : 0) + (showItem ? 1 : 0);

    return (
        <div ref={rootRef} className="print-area ledger-area" style={SHEET_ROOT_STYLE}>
            {/* 제목 */}
            <h1 style={{ textAlign: 'center', fontSize: '24px', fontWeight: '700', marginBottom: '24px', letterSpacing: '4px' }}>
                {title}
            </h1>

            {/* 번호/기간 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', fontSize: '14px' }}>
                <span><strong>명세서번호:</strong> {statement.statementNumber || '-'}</span>
                <span><strong>기간:</strong> {formatStatementDate(statement.startDate)} ~ {formatStatementDate(statement.endDate)}</span>
            </div>

            {/* 공급자/공급받는자 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
                <div style={{ padding: '16px', background: '#F8FAFC', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '8px', fontWeight: '600' }}>공급자</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>{statement.fromUserName || statement.supplierInfo?.businessName || '-'}</div>
                    {statement.supplierInfo?.representativeName && (
                        <div style={{ fontSize: '12px', color: '#475569', marginTop: 4 }}>대표 {statement.supplierInfo.representativeName}</div>
                    )}
                    {statement.supplierInfo?.businessNumber && (
                        <div style={{ fontSize: '12px', color: '#475569', marginTop: 2 }}>사업자 {statement.supplierInfo.businessNumber}</div>
                    )}
                    {statement.supplierInfo?.bankAccount?.accountNumber && (
                        <div style={{ marginTop: 10, padding: '8px 10px', background: '#EEF2FF', borderRadius: 6, fontSize: 13 }}>
                            <div style={{ fontSize: 11, color: '#4F46E5', fontWeight: 700, marginBottom: 2 }}>이체 계좌</div>
                            <div style={{ color: '#0f172a' }}>
                                <strong>{statement.supplierInfo.bankAccount.bankName || ''}</strong> {statement.supplierInfo.bankAccount.accountNumber}
                            </div>
                            {statement.supplierInfo.bankAccount.accountHolder && (
                                <div style={{ fontSize: 12, color: '#475569' }}>예금주 {statement.supplierInfo.bankAccount.accountHolder}</div>
                            )}
                        </div>
                    )}
                </div>
                <div style={{ padding: '16px', background: '#F8FAFC', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '8px', fontWeight: '600' }}>공급받는자</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>{statement.toUserName || '-'}</div>
                </div>
            </div>

            {beforeTable}

            {/* 항목 테이블 — 줄공책 스타일 */}
            <table className="ledger-table" {...(tableProps || {})} style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '13px' }}>
                <thead>
                    <tr>
                        {headLead}
                        <th style={{ textAlign: 'left' }}>작성일</th>
                        {/* 의뢰서 납기 — 글자만 '납기일'(2026-10-23 §5-3). 데이터는 그대로 dueDate */}
                        <th style={{ textAlign: 'left' }}>납기일</th>
                        <th style={{ textAlign: 'left' }}>환자이름</th>
                        <th style={{ textAlign: 'left', minWidth: showItem ? '90px' : '160px' }}>치아정보</th>
                        {showItem && <th style={{ textAlign: 'left', minWidth: '180px' }}>품목</th>}
                        <th style={{ textAlign: 'center' }}>개수</th>
                        <th style={{ textAlign: 'right' }}>개당가격</th>
                        <th style={{ textAlign: 'right' }}>합산가격</th>
                        {headTrail}
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, index) => {
                        const flagged = !!(highlight && highlight(index)) || disputedIndexMap[index] !== undefined;
                        const patientNode = patient ? patient(item, index) : undefined;
                        const toothNode = tooth ? tooth(item, index) : undefined;
                        const countNode = count ? count(item, index) : undefined;
                        const unitNode = unitPrice ? unitPrice(item, index) : undefined;
                        return (
                            <React.Fragment key={index}>
                                {/* flagged-row: 인쇄(printStatement iframe CSS)에서 강조 배경 제거 (2026-10-24 재점검 P1-5) */}
                                <tr className={flagged ? 'flagged-row' : undefined} style={{ borderBottom: flagged ? 'none' : '1px solid #F1F5F9', background: flagged ? '#FFFBEB' : 'transparent' }}>
                                    {lead && lead(item, index)}
                                    <td style={{ padding: '8px' }}>
                                        {formatStatementShortDate(item.createdAt)}
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        {formatStatementShortDate(item.dueDate)}
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        {patientNode !== undefined ? patientNode : (item.patientName || '-')}
                                        {showSourceBadges && item.source === 'auto' && (
                                            <span className="no-print" style={{ display: 'inline-block', marginLeft: 5, fontSize: 10, fontWeight: 800, background: '#EEF2FF', color: '#4338CA', padding: '1px 7px', borderRadius: 5, verticalAlign: 'middle' }}>자동수집</span>
                                        )}
                                        {showSourceBadges && item.source === 'manual' && (
                                            <span className="no-print" style={{ display: 'inline-block', marginLeft: 5, fontSize: 10, fontWeight: 800, background: '#F1F5F9', color: '#64748B', padding: '1px 7px', borderRadius: 5, verticalAlign: 'middle' }}>수기</span>
                                        )}
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        {/* 금값 파생 행(itemType 'gold') — 발행본·인쇄에도 중량·시세·면세가 그대로 남도록 표기 고정.
                                            수정은 작성 화면의 중량·시세 입력으로만 (여기서 문구를 고치면 금액과 어긋남) */}
                                        {item.itemType === 'gold' ? (
                                            <>
                                                <span style={{ color: '#92400E', fontWeight: 600 }}>{'└ '}{item.toothInfo || '금값'}</span>
                                                <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', padding: '1px 6px', borderRadius: 5, verticalAlign: 'middle' }}>면세</span>
                                            </>
                                        ) : toothNode !== undefined ? toothNode : (item.toothInfo || '-')}
                                        {/* 수리 배지 (2026-09-04 C-5): remakeKind로만 표시, toothInfo 접미 없음 */}
                                        {item.remakeKind === 'repair' && (
                                            <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, background: '#ECFEFF', color: '#0E7490', padding: '2px 8px', borderRadius: 6 }}>수리</span>
                                        )}
                                        {/* 디자인 완료/전 배지 (2026-09-06) — 저장된 designState만 사용, 구데이터(값 없음)는 배지 생략. 인쇄는 소형 글자 */}
                                        {item.itemType !== 'gold' && <DesignBadge badge={designBadgeFromSaved(item.designState)} style={{ marginLeft: 6 }} />}
                                        {/* 리메이크 '처음 작업' 표기 (2026-10-24 2단계 ④ · 계약 §1): 치과에 가는 종이라 날짜·번호만. 책임 구분(remakeLiability)은 기공소 내부 값 — 여기서 절대 출력하지 않는다 */}
                                        {item.itemType !== 'gold' && remakeOfLabel(item) && (
                                            <div style={{ marginTop: 2, fontSize: '11px', color: '#64748B', lineHeight: 1.3 }}>{remakeOfLabel(item)}</div>
                                        )}
                                    </td>
                                    {showItem && (
                                        <td style={{ padding: '8px' }}>{tidyServiceName(item.serviceName || item.name) || '-'}</td>
                                    )}
                                    <td style={{ padding: '8px', textAlign: 'center' }}>
                                        {countNode !== undefined && item.itemType !== 'gold' ? countNode : (
                                            <span style={{ display: 'inline-block', padding: '2px 8px', background: '#DBEAFE', color: '#1D4ED8', borderRadius: '10px', fontSize: '12px', fontWeight: '600' }}>
                                                {item.toothCount || 0}
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'right' }}>
                                        {unitNode !== undefined && item.itemType !== 'gold' ? unitNode : (item.pricePerTooth || item.unitPrice || 0).toLocaleString()}
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: '500' }}>
                                        {(item.price || 0).toLocaleString()}
                                    </td>
                                    {trail && trail(item, index)}
                                </tr>
                                {after && after(item, index)}
                                {/* 수정 사유 읽기전용 표시 (발급자 화면) */}
                                {showDisputeRows && disputedIndexMap[index] !== undefined && (
                                    <tr className="no-print" style={{ background: '#FFFBEB', borderBottom: '1px solid #FDE68A' }}>
                                        <td colSpan={disputeColSpan} style={{ padding: '4px 8px 10px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <MessageSquare size={14} color="#92400E" style={{ flexShrink: 0 }} />
                                                <span style={{ fontSize: '13px', color: '#92400E' }}>
                                                    {disputedIndexMap[index] || '수정 요청 중'}
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>

            {afterTable}

            {/* 합계 */}
            <div style={{ borderTop: '2px solid #1E293B', paddingTop: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxWidth: '300px', marginLeft: 'auto' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                        <span style={{ color: '#64748B' }}>소계 ({totals.totalTeeth}개)</span>
                        <span>{totals.subtotal.toLocaleString()}원</span>
                    </div>
                    {totals.discountAmount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#EF4444' }}>
                            <span>할인</span>
                            <span>-{totals.discountAmount.toLocaleString()}원</span>
                        </div>
                    )}
                    <div style={{ borderTop: '2px solid #E2E8F0', paddingTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: '700', fontSize: '16px' }}>합계</span>
                        <span style={{ fontWeight: '700', fontSize: '20px', color: '#10B981' }}>{totals.total.toLocaleString()}원</span>
                    </div>
                    {/* 부가세 안내 1줄 — 법적보안 문구 확정 전(빈 문자열)에는 렌더하지 않음 (골드 P1-4). vatNote prop으로 대체·숨김 가능 */}
                    {vatNote && (
                        <div style={{ fontSize: '12px', color: '#64748B', textAlign: 'right', marginTop: '4px' }}>{vatNote}</div>
                    )}
                </div>
            </div>

            {/* 전월 미수 안내 (2026-10-23 1단계 §4-5) — 합계 아래 별도 상자, 합계에 더하지 않음. 치과에 가는 종이라 인쇄에도 나온다 */}
            <CarryOverBox carryOver={statement.carryOver} labName={statement.fromUserName || statement.supplierInfo?.businessName} />

            {/* 비고 */}
            {statement.notes && !hideNotes && (
                <div style={{ marginTop: '20px', padding: '12px', background: '#F8FAFC', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '4px', fontWeight: '600' }}>비고</div>
                    <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{statement.notes}</div>
                </div>
            )}

            {afterNotes}

            {/* 수정 요청 이력 */}
            {lastDisputeNote && !hideDisputeHistory && (
                <div className="no-print" style={{
                    marginTop: '20px', padding: '16px',
                    background: statement.settlementStatus === 'disputed' ? '#FEF2F2' : '#F0FDF4',
                    border: `1px solid ${statement.settlementStatus === 'disputed' ? '#FECACA' : '#BBF7D0'}`,
                    borderRadius: '10px',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        {statement.settlementStatus === 'disputed' ? (
                            <AlertTriangle size={18} color="#DC2626" />
                        ) : (
                            <CheckCircle size={18} color="#16A34A" />
                        )}
                        <span style={{ fontSize: '14px', fontWeight: '700', color: statement.settlementStatus === 'disputed' ? '#DC2626' : '#16A34A' }}>
                            {statement.settlementStatus === 'disputed' ? '수정 요청 중' : '수정 요청 이력'}
                        </span>
                        <span style={{ fontSize: '12px', color: '#64748B', marginLeft: 'auto' }}>
                            {lastDisputeNote.authorName} · {new Date(lastDisputeNote.createdAt).toLocaleDateString('ko-KR')}
                        </span>
                    </div>
                    {lastDisputeNote.checkedItems?.map((ci, idx) => (
                        <div key={idx} style={{
                            display: 'flex', gap: '8px', padding: '8px 12px',
                            background: '#fff', borderRadius: '6px', marginBottom: '6px',
                            border: '1px solid #E2E8F0', fontSize: '13px',
                        }}>
                            <span style={{ fontWeight: '600', color: '#334155', minWidth: '100px' }}>
                                {ci.patientName} — {ci.toothInfo}
                            </span>
                            <span style={{ color: '#64748B' }}>|</span>
                            <span style={{ color: '#DC2626' }}>{ci.reason}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* 정산 완료 표시 */}
            {statement.settlementStatus === 'settled' && statement.settledAt && (
                <div className="no-print" style={{
                    marginTop: '20px', padding: '12px 16px',
                    background: '#F0FDF4', border: '1px solid #BBF7D0',
                    borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px',
                    fontSize: '14px', color: '#065F46',
                }}>
                    <CheckCircle size={18} color="#059669" />
                    <span>
                        {statement.settledByName || '수신자'}이(가) 정산을 확인했습니다. (정산 완료)
                        ({new Date(statement.settledAt).toLocaleDateString('ko-KR')})
                    </span>
                </div>
            )}
        </div>
    );
}

export default StatementSheet;
